import { useCallback, useEffect, useMemo } from 'react';
import { selectBrands_audience_to_save, select_change_flag } from 'store/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { ModalBase, ModalHeader, ModalFooter, SelectedBase } from 'components';
import * as S from './ChooseBrandsModels.styled';
import { useToggleState } from './useToggleState';
import { useSet } from './useSet.js';
import { useFacade } from './useFacade';
import ChooseContent from './ChooseContent';
import { transformInc } from 'utils';
import SelectContent from './components/selectContent/SelectContent';
import { targetingBrandsModelsActions } from 'store/actions';

const toggleSet = (element) => (set) => {
  if (set.has(element)) {
    return set.delete(element);
  }
  set.add(element);
};

const ChooseBrandsModels = ({ isOpen, close, title }) => {
  //data_form_server
  const { inc, exc } = useSelector(selectBrands_audience_to_save);
  const { InitIncBrands, InitIncModels } = transformInc(inc);

  const dispatch = useDispatch();
  const {
    toggle: toggleOpened,
    isActive: isOpened,
    toggleOn: openBrand,
    toggleOff: closeBrand,
    state: openedBrands,
  } = useToggleState();

  const initExcModels = useMemo(
    () => (exc ? exc?.split(',').map((model) => Number(model.split(':')[1])) : []),
    [exc],
  );
  const [includeSet, setIncludeSet] = useSet([...InitIncBrands, ...InitIncModels]);
  const [excludeSet, setExcludeSet] = useSet(initExcModels);

  const incSize = Boolean(includeSet.size);
  const dataChangingFlag = useSelector(select_change_flag) || !incSize;
  const {
    brandToCounters,
    counterToBrand,
    faundedBrandsWithSelection,
    debouncedValue,
    search,
    setSearch,
    handleSaveBtn,
  } = useFacade(openBrand, includeSet, excludeSet, close);

  const toggle = (brandOrCounter) => {
    dispatch(targetingBrandsModelsActions.changeFlag(false));
    const isCounter = typeof brandOrCounter === 'number';
    const brand = isCounter ? counterToBrand[brandOrCounter] : brandOrCounter;
    if (!isCounter) {
      setExcludeSet((s) => brandToCounters[brand].forEach((counter) => s.delete(counter)));
      setIncludeSet((s) => brandToCounters[brand].forEach((counter) => s.delete(counter)));
      return setIncludeSet(toggleSet(brand));
    }
    if (includeSet.has(brand)) {
      return setExcludeSet((newExcludeSet) => {
        toggleSet(brandOrCounter)(newExcludeSet);
        const brandCounters = [...brandToCounters[brand]];
        if (brandCounters.some((counter) => !newExcludeSet.has(counter))) {
          return;
        }
        brandCounters.forEach((counter) => newExcludeSet.delete(counter));
        setIncludeSet((newIncludeSet) => newIncludeSet.delete(brand));
      });
    }

    setIncludeSet((newIncludeSet) => {
      toggleSet(brandOrCounter)(newIncludeSet);
      const brandCounters = [...brandToCounters[brand]];
      if (brandCounters.some((counter) => !newIncludeSet.has(counter))) {
        return;
      }
      brandCounters.forEach((counter) => newIncludeSet.delete(counter));
      newIncludeSet.add(brand);
    });
  };

  useEffect(() => {
    if (!debouncedValue) {
      const selectedBrands = faundedBrandsWithSelection
        .filter(({ isSelected }) => isSelected)
        .map(({ brand }) => brand);
      openedBrands.forEach((brand) => closeBrand(brand));
      selectedBrands.forEach((brand) => openBrand(brand));
    } else {
      const faundedBrands = faundedBrandsWithSelection.map(({ brand }) => brand);
      faundedBrands.forEach((brand) => openBrand(brand));
    }
  }, [debouncedValue]);

  useEffect(() => {
    return () => {
      dispatch(targetingBrandsModelsActions.changeFlag(true));
    };
  }, []);

  const clearIncludeItems = useCallback(() => {
    setIncludeSet((newIncludeSet) => newIncludeSet.clear());
    setExcludeSet((newIncludeSet) => newIncludeSet.clear());
    dispatch(targetingBrandsModelsActions.changeFlag(false));
  }, []);

  const clearExcludeItems = useCallback(() => {
    setExcludeSet((newIncludeSet) => newIncludeSet.clear());
    dispatch(targetingBrandsModelsActions.changeFlag(false));
  }, []);

  const deleteIncAction = useCallback((brandOrCounter) => {
    const isCounter = typeof brandOrCounter === 'number';
    if (isCounter) {
      setIncludeSet((newIncludeSet) => newIncludeSet.delete(brandOrCounter));
    }
    if (!isCounter) {
      setIncludeSet((newIncludeSet) => newIncludeSet.delete(brandOrCounter));
      setExcludeSet((s) => brandToCounters[brandOrCounter].forEach((counter) => s.delete(counter)));
    }
  }, []);

  const deleteExcAction = useCallback((counter) => {
    setExcludeSet((newIncludeSet) => newIncludeSet.delete(counter));
  }, []);

  return (
    <ModalBase isOpen={isOpen}>
      <S.Box>
        <ModalHeader
          title={title}
          close={close}
          message='Select a Brand to include the Brand, unselect a Model within the selected Brand to exclude.'
        />
        <S.Container>
          <ChooseContent
            brandsWithSelection={faundedBrandsWithSelection}
            isOpened={isOpened}
            toggle={toggle}
            toggleOpened={toggleOpened}
            search={search}
            setSearch={setSearch}
          />
          <S.Content>
            <SelectedBase
              excludeSet={[...excludeSet]}
              includeSet={[...includeSet]}
              clearIncludeItems={clearIncludeItems}
              clearExcludeItems={clearExcludeItems}
              setIncludeSet={setIncludeSet}
              setExcludeSet={setExcludeSet}
            >
              <SelectContent
                excludeSet={[...excludeSet]}
                includeSet={[...includeSet]}
                deleteIncAction={deleteIncAction}
                deleteExcAction={deleteExcAction}
              />
            </SelectedBase>
          </S.Content>
        </S.Container>
        <ModalFooter
          saveButtonText='Apply'
          saveButtonOnClick={handleSaveBtn}
          close={close}
          saveButtonDisabled={dataChangingFlag}
        />
      </S.Box>
    </ModalBase>
  );
};

export default ChooseBrandsModels;
